import { byNumericId } from './id-sort.ts';
import type { AddonFilterConfig } from './schema.ts';

function isPlainObject(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

function serializeConfigValue(v: unknown, indent: number): string {
  if (typeof v === 'string') return JSON.stringify(v);
  if (Array.isArray(v)) {
    if (v.length === 0) return '[]';
    const items = v.map(x => serializeConfigValue(x, indent)).join(', ');
    return `[${items}]`;
  }
  if (isPlainObject(v)) {
    const keys = Object.keys(v).sort(byNumericId);
    if (keys.length === 0) return '{}';
    const inner = keys.map(k => {
      const val = serializeConfigValue(v[k], indent + 2);
      return `${' '.repeat(indent + 2)}${JSON.stringify(k)}: ${val}`;
    }).join(',\n');
    return `{\n${inner}\n${' '.repeat(indent)}}`;
  }
  return JSON.stringify(v);
}

export function withCacheBust(url: string, timestamp?: number): string {
  if (!url) return url;
  const t = timestamp ?? Date.now();
  try {
    const u = new URL(url);
    u.searchParams.set('v', String(t));
    return u.toString();
  } catch {
    // Fallback for non-absolute URLs: append/replace query string manually
    const base = url.split('?')[0];
    const rest = url.slice(base.length);
    const params = new URLSearchParams(rest);
    params.set('v', String(t));
    const qs = params.toString();
    return qs ? `${base}?${qs}` : base;
  }
}

export function generateConfigJs(config: AddonFilterConfig): string {
  const body = Object.keys(config).map(k => {
    const val = serializeConfigValue(config[k as keyof AddonFilterConfig], 2);
    return `  ${k}: ${val}`;
  }).join(',\n');
  return `window.addonFilterConfig = {\n${body}\n};`;
}

export function generateCmsSnippet(config: AddonFilterConfig, engineUrl: string, cssUrl: string): string {
  const configJs = generateConfigJs(config);
  return `<link rel="stylesheet" href="${withCacheBust(cssUrl)}">\n<script>${configJs}</script>\n<script src="${withCacheBust(engineUrl)}"></script>`;
}
